import { Box, Typography } from "@mui/material";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

const formatSecondsToHumanReadable = (totalSeconds: number): string => {
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const parts: string[] = [];
	if (hours > 0) {
		parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
	}
	if (minutes > 0) {
		parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
	}
	if (seconds > 0 || parts.length === 0) {
		parts.push(`${seconds} second${seconds !== 1 ? "s" : ""}`);
	}
	return parts.join(" ");
};

interface FormTimerWrapperProps {
	children: ReactNode;
	maxAllowedTimeMinutes: number | null;
	onTimerExpired: () => void;
}

const FormTimerActive = ({
	children,
	maxAllowedTimeMinutes,
	onTimerExpired,
}: {
	children: ReactNode;
	maxAllowedTimeMinutes: number;
	onTimerExpired: () => void;
}) => {
	const safeDuration = maxAllowedTimeMinutes > 0 ? maxAllowedTimeMinutes : 10;
	const [remainingSeconds, setRemainingSeconds] = useState(safeDuration * 60);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const expiredRef = useRef(false);

	const clearCountdownInterval = useCallback(() => {
		if (intervalRef.current !== null) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	useEffect(() => {
		intervalRef.current = setInterval(() => {
			setRemainingSeconds((prev) => {
				const next = prev - 1;
				if (next <= 0) {
					clearCountdownInterval();
					if (!expiredRef.current) {
						expiredRef.current = true;
						onTimerExpired();
					}
				}
				return Math.max(next, 0);
			});
		}, 1000);

		return () => {
			clearCountdownInterval();
		};
	}, [clearCountdownInterval, onTimerExpired]);

	return (
		<>
			<Box
				data-testid="form-timer-overlay"
				sx={{
					position: "sticky",
					top: 0,
					zIndex: 9999,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					backgroundColor: "rgba(0, 0, 0, 0.75)",
					py: 1,
				}}
			>
				<Typography
					variant="body1"
					data-testid="countdown-display"
					sx={{ color: "#fff", fontWeight: "bold" }}
				>
					Time remaining: {formatSecondsToHumanReadable(remainingSeconds)}
				</Typography>
			</Box>
			{children}
		</>
	);
};

export const FormTimerWrapper = ({
	children,
	maxAllowedTimeMinutes,
	onTimerExpired,
}: FormTimerWrapperProps) => {
	if (maxAllowedTimeMinutes === null) {
		return <>{children}</>;
	}

	return (
		<FormTimerActive
			maxAllowedTimeMinutes={maxAllowedTimeMinutes}
			onTimerExpired={onTimerExpired}
		>
			{children}
		</FormTimerActive>
	);
};
